import { createRoot } from 'react-dom/client';
import { Preloader } from './utils/Preloader';
import { SpineLayout } from '@pixi/spine-layout';
import { Application, isMobile, Sprite, Texture, TilingSprite } from 'pixi.js';
import { getPlayerSessionId } from './helpers/playerSessionId';
import { GameController } from './controllers/GameController';
import { addCheats } from './controllers/cheats';
import StompConnector from './controllers/StompConnector';
import manifest from './settings/assetsManifest.json';
import '@esotericsoftware/spine-pixi-v8';
import './main.css';
import { config } from './settings/config';
import { UI } from './ui';
import { sounds } from './utils/Sounds';
import { useSettings } from '@/store/settingsStore';

class App {
    layout?: SpineLayout;
    pixi = new Application();
    debug = false;
    gameController!: GameController;
    crowd!: TilingSprite;
    crowdBotttom!: Sprite;

    constructor() {
        // TODO: if development mode
        getPlayerSessionId();

        this.init();
    }

    async init() {
        await new Preloader('assets').load(manifest, ['sprites', 'spines'], (progress: number) => {
            if (window.setLoadingProgress) {
                window.setLoadingProgress(progress);
            }
        });

        await this.initPixi();

        await StompConnector.init();

        createRoot(document.getElementById('root')!).render(<UI />);

        this.layout = new SpineLayout({
            debug: this.debug,
            skipAttachingSpinesPatterns: ['Horse-'],
        });
        await this.layout.createInstancesFromManifest(manifest, 'spines');

        this.crowd = TilingSprite.from('crowd');
        this.crowd.tilePosition.set(0, -3);
        this.crowd.height = 280;

        this.crowdBotttom = Sprite.from(Texture.WHITE);
        this.crowdBotttom.tint = config.bgColor;
        this.crowdBotttom.width = window.innerWidth;
        this.crowdBotttom.height = 100;

        this.pixi.stage.addChild(this.crowdBotttom, this.layout, this.crowd);

        this.gameController = new GameController(this.layout);

        // TODO: if development mode
        addCheats(this.pixi, this.layout);

        window.addEventListener('resize', () => this.onResize());

        this.onResize();

        this.initSounds();
    }

    private async initSounds() {
        sounds.init(manifest, {
            musicMuted: false,
            fxMuted: false,
            muted: useSettings.getState().muted,
            debug: true,
            musicVolume: 0.8,
            fxVolume: 0.8,
        });
    }

    private async initPixi() {
        await this.pixi.init({
            resizeTo: window,
            backgroundColor: config.bgColor,
        });

        document.getElementById('pixi-container')!.appendChild(this.pixi.canvas);
    }

    private onResize = () => {
        if (this.layout) {
            this.layout.x = window.innerWidth / 2;
            this.layout.y = 545;

            const portraitMode = window.innerHeight > window.innerWidth;
            const layoutHeight = 2700;

            if (portraitMode) {
                const minHeight = isMobile.phone ? 1350 : 1220;

                if (window.innerHeight < minHeight) {
                    this.layout.scale.set(window.innerHeight / minHeight);
                }

                this.layout.y = 545 * this.layout.scale.y;
            } else {
                const minHeight = window.innerHeight * 1.9;

                if (layoutHeight > minHeight) {
                    this.layout.scale.set(minHeight / layoutHeight);

                    this.layout.y = 545 * this.layout.scale.y;
                }
            }
        }

        this.crowd.y = window.innerHeight - this.crowd.height;
        this.crowd.width = window.innerWidth;
        this.crowdBotttom.width = window.innerWidth;
        this.crowdBotttom.y = this.crowd.y + this.crowd.height - 40;
        this.crowdBotttom.height = window.innerHeight - this.crowdBotttom.y + 100;
    };
}

export const app = new App();

declare global {
    interface Window {
        setLoadingProgress?: (progress: number) => void;
        Util: {
            setCurrency: (currency: string) => void;
        };
    }
}
